import { useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Screen } from "@/components/screen";
import { Card, Pill } from "@/components/ui";
import { getProviderConnections, hasConnectedProvider } from "@/features/sync/connections";

const APP_STORE_URL = "https://apps.apple.com/app/id6781928157";
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.footslapping";

const ONBOARDING_STEPS = [
  {
    emoji: "👟",
    title: "Connect your step source",
    description:
      "FootSlapping reads steps from Apple Health or Health Connect in the iOS/Android app. Download the app to get started.",
  },
  {
    emoji: "🏆",
    title: "Join or create competitions",
    description:
      "Once connected, you can accept invites, join with a code, or launch your own step challenge.",
  },
  {
    emoji: "📊",
    title: "Watch the leaderboard update",
    description:
      "Your steps sync automatically so you and your friends can see live standings throughout the competition.",
  },
];

export default function HealthSetup() {
  const navigate = useNavigate();
  const search = useSearch({ from: "/health-setup" }) as { redirect?: string };
  const redirectTo = search.redirect ?? "/dashboard";
  const [checking, setChecking] = useState(true);
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    (async () => {
      const connections = await getProviderConnections();
      const isConnected = hasConnectedProvider(connections);
      setConnected(isConnected);
      setChecking(false);
      if (isConnected) {
        navigate({ to: redirectTo, replace: true });
      }
    })();
  }, [navigate, redirectTo]);

  function handleSkip() {
    navigate({ to: redirectTo, replace: true });
  }

  if (checking || connected) {
    return (
      <Screen title="Checking health connection..." subtitle="One moment while we look you up.">
        <Card>
          <div className="animate-pulse h-4 bg-[rgba(28,27,46,0.08)] rounded w-3/4" />
          <div className="animate-pulse h-4 bg-[rgba(28,27,46,0.08)] rounded w-1/2" />
          <div className="animate-pulse h-10 bg-[rgba(28,27,46,0.08)] rounded" />
          <div className="animate-pulse h-10 bg-[rgba(28,27,46,0.08)] rounded" />
        </Card>
      </Screen>
    );
  }

  return (
    <Screen
      eyebrow="Setup"
      title="Connect a health provider"
      subtitle="FootSlapping needs at least one step source so your competitions have scores to rank."
    >
      <Card tone="accent">
        <div className="flex flex-col gap-2">
          <span className="text-text text-[1.0625rem] font-bold">Why this matters</span>
          <p className="text-text-muted text-sm leading-5">
            Competitions are scored from the steps you sync. Without a connected health provider,
            you can&apos;t be added to a competition. Your data is only used for leaderboards and
            never shared outside your competitions.
          </p>
        </div>
      </Card>

      <div className="flex flex-col gap-3">
        {ONBOARDING_STEPS.map((step) => (
          <Card key={step.title} tone="strong">
            <div className="flex flex-row gap-3 items-start">
              <span className="text-2xl" role="img" aria-label={step.title}>
                {step.emoji}
              </span>
              <div className="flex flex-col gap-1">
                <span className="text-text text-sm font-bold">{step.title}</span>
                <span className="text-text-muted text-sm leading-5">{step.description}</span>
              </div>
            </div>
          </Card>
        ))}
      </div>

      <Card>
        <Pill label="Mobile app required" tone="warning" />
        <p className="text-text-muted text-sm leading-5">
          Step syncing happens through the FootSlapping app now — Apple Health on iOS, or Health
          Connect on Android. Download the app and connect a step source there.
        </p>
        <div className="flex flex-row gap-3">
          <a
            href={APP_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-aqua rounded-2xl py-3.5 px-6 items-center justify-center shadow-[1px_1px_0px_0px_#1c1b2e] border border-text no-underline inline-flex font-inherit cursor-pointer"
          >
            <span className="text-white font-extrabold text-sm">Get the iOS app</span>
          </a>
          <a
            href={PLAY_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-aqua rounded-2xl py-3.5 px-6 items-center justify-center shadow-[1px_1px_0px_0px_#1c1b2e] border border-text no-underline inline-flex font-inherit cursor-pointer"
          >
            <span className="text-white font-extrabold text-sm">Get the Android app</span>
          </a>
        </div>
      </Card>

      <button
        type="button"
        onClick={handleSkip}
        className="text-text-muted text-sm font-bold underline-offset-2 hover:underline cursor-pointer bg-transparent border-0"
      >
        I&apos;ll connect later — continue to dashboard
      </button>
    </Screen>
  );
}
